<template>
  <section class="activity-stage build-a-keyboard">
    <div id="dragging-content">
      <keyboard-k2-lite></keyboard-k2-lite>

      <div
        v-if="currentStep"
        class="draggable-key-container"
        :class="{ scatter: currentStep.scattered }"
      ></div>
    </div>
  </section>
</template>

<script>
import { TweenMax, TimelineLite, Power1 } from 'gsap';
import Draggable from 'gsap/Draggable';
import lodash from 'lodash';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

import KeyboardK2Lite from 'components/keyboards/KeyboardK2Lite.vue';

export default {
  components: {
    KeyboardK2Lite
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'BuildAKeyboard',
      keyGroupSnapped: 0,
      droppables: '',
      draggableLetters: [],
      dragOriginalPosition: [],
      dropArea: [],
      dropXPosition: 0,
      dropYPosition: 0,
      dragSourceCoordinatesX: 0,
      dragSourceCoordinatesY: 0,
      resultCurrentPositionX: 0,
      resultCurrentPositionY: 0,
      dynamicMargin: 0
    };
  },
  computed: {
    currentStep() {
      return this.currentRound && this.currentRound.steps ? this.currentRound.steps[0] : null;
    }
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Move the keys back onto the keyboard.',
          voice: `${this.voiceInstructionsPrefix}/buildakeyboard-${this.getRandomVoice()}-1.mp3`,
          delay: 4
        }
      };
    },

    startActivity() {
      this.enableKeyboard();
      this.errorLoggerStart();

      this.startRound();
    },

    startRound() {
      this.setupNewRound();

      TweenMax.set('.build-a-keyboard', { autoAlpha: 1 });
      TweenMax.delayedCall(1, () => {
        this.extractKeys();
        this.enableDragging();
      });
    },

    newRound() {
      this.setupNewRound();

      this.$nextTick(() => {
        this.extractKeys();
        this.enableDragging();
      });
    },

    getOrderedKeyGroups(unorderedKeyGroups) {
      const firstRow = ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '+'];
      const secondRow = [
        'Tab',
        'Q',
        'W',
        'E',
        'R',
        'T',
        'Y',
        'U',
        'I',
        'O',
        'P',
        'Bracket1',
        'Bracket2',
        'BSlash'
      ];
      const thirdRow = [
        'CapLock',
        'A',
        'S',
        'D',
        'F',
        'G',
        'H',
        'J',
        'K',
        'L',
        'Semicolon',
        'Enter'
      ];
      const fourthRow = ['Shift1', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', 'FSlash', 'Shift2'];
      const fifthRow = ['Space'];
      const rows = [firstRow, secondRow, thirdRow, fourthRow, fifthRow];

      // eslint-disable-next-line
      return unorderedKeyGroups.sort((left, right) => {
        for (let i = 0; i < rows.length; i += 1) {
          for (let j = 0; j < rows.length; j += 1) {
            if (
              lodash.find(rows[i], el => el === left.split(',')[0]) &&
              lodash.find(rows[j], el => el === right.split(',')[0])
            ) {
              if (i < j) {
                return -1;
              }
              if (i === j) {
                return 0;
              }
              if (i > j) {
                return 1;
              }
            }
          }
        }
      });
    },

    extractKeys() {
      const targetedKeyGroups = this.getOrderedKeyGroups(this.currentStep.letters);
      const scatteredKeys = this.currentStep.scattered;
      const draggableKeyContainer = document.getElementsByClassName('draggable-key-container')[0];
      const splitKeyGroups = targetedKeyGroups.map(keyGroup => keyGroup.split(','));
      let parentRowContainer = null;
      let keyGroupContainer = null;
      let keyGroupContainerWidth = 0;
      let keyHeight = 0;
      let processedCurrentKeyEl = null;
      let keyElsToHide = null;
      let previousKeyGroupContainer = null;
      let rowSeparator = null;

      splitKeyGroups.forEach(splitKeyGroup => {
        keyElsToHide = [];
        keyGroupContainerWidth = 0;
        keyGroupContainer = document.createElement('div');
        keyGroupContainer.className = `target-Key${splitKeyGroup[0]} drag-group`;

        splitKeyGroup.forEach((keyName, index) => {
          const currentKeyEl =
            keyName === 'Shift'
              ? document.getElementsByClassName('KeyShift1')[0]
              : document.getElementsByClassName(`Key${keyName}`)[0];

          if (index === 0) {
            keyHeight = currentKeyEl.offsetHeight;
            parentRowContainer = currentKeyEl.parentNode;
            keyGroupContainer.className += ` ${parentRowContainer.className}`;
          }

          keyGroupContainerWidth += currentKeyEl.offsetWidth;
          processedCurrentKeyEl = currentKeyEl.cloneNode(true);
          processedCurrentKeyEl.className += ' displaced-key';

          keyGroupContainer.appendChild(processedCurrentKeyEl);

          keyElsToHide.push(currentKeyEl);
        });

        this.styleKeyGroupContainer(keyGroupContainer, keyGroupContainerWidth, keyHeight);

        this.hideKeyGroupAndInjectPlaceholder(keyElsToHide, parentRowContainer, keyGroupContainer);

        if (scatteredKeys) {
          this.setScatterStyle(keyGroupContainer, keyHeight);
        }

        if (previousKeyGroupContainer) {
          if (
            previousKeyGroupContainer.className.match(/row[0-4]/)[0] !==
            keyGroupContainer.className.match(/row[0-4]/)[0]
          ) {
            rowSeparator = document.createElement('div');
            rowSeparator.className = 'row-separator';
            draggableKeyContainer.appendChild(rowSeparator);
          }
        }

        keyGroupContainer.style.width = `${keyGroupContainerWidth + 2}px`;

        draggableKeyContainer.appendChild(keyGroupContainer);
        previousKeyGroupContainer = keyGroupContainer;
      });

      this.centerDraggableKeyContainerVertically();
    },

    styleKeyGroupContainer(element, width, height) {
      const elementToChange = element;

      elementToChange.style.width = `${width}px`;
      elementToChange.style.height = `${height}px`;
    },

    setScatterStyle(keyGroupContainer, keyHeight) {
      const scatterGroup = keyGroupContainer;

      scatterGroup.style.top = `${lodash.random(0, 370 - keyHeight)}px`;
      scatterGroup.style.left = `${lodash.random(
        0,
        1000 - keyGroupContainer.childNodes.length * keyHeight
      )}px`;
    },

    centerDraggableKeyContainerVertically() {
      const draggableKeyContainer = document.getElementsByClassName('draggable-key-container')[0];
      const keyboardWrapper = document.getElementsByClassName('keyboard-wrapper')[0];
      const contentPanel = document.getElementsByClassName('contentPanel')[0];

      const contentPanelBottom = contentPanel.getBoundingClientRect().bottom;
      const keyboardWrapperBottom = keyboardWrapper.getBoundingClientRect().bottom;
      const draggableKeyContainerHeight = draggableKeyContainer.getBoundingClientRect().height;
      const dynamicMargin =
        (contentPanelBottom - keyboardWrapperBottom - draggableKeyContainerHeight) / 2;

      draggableKeyContainer.style.marginTop =
        this.isMobile && this.device.usingExternalKeyboard ? '5px' : `${dynamicMargin}px`;
    },

    hideKeyGroupAndInjectPlaceholder(keyElsToRemove, rowEl, placeholderSource) {
      const placeholderToInject = this.getPlaceholder(placeholderSource);
      const keysToRemove = keyElsToRemove;

      keyElsToRemove.forEach((el, index) => {
        if (index === 0) {
          rowEl.insertBefore(placeholderToInject, keyElsToRemove[0]);
        }

        keysToRemove[index].className += ' hide';
      });
    },

    getPlaceholder(source) {
      const placeholder = source.cloneNode();
      const placeholderClassName = source.className.match(/Key[^\s]+/)[0];

      placeholder.className = `placeholder-${placeholderClassName} drag-source`;
      placeholder.style.display = 'inline-block';
      placeholder.style.width = source.style.width;
      placeholder.style.height = source.style.height;

      return placeholder;
    },

    enableDragging() {
      const self = this;
      const draggableItems = [];
      const { isMobile } = this.userDevice;
      const tLite = new TimelineLite();
      let bounds = null;

      this.droppables = document.getElementsByClassName('drag-group');
      this.getPositionOfItems(this.droppables);
      this.getDropZone();

      Array.from(this.droppables).forEach(droppable => {
        let hasHitBoundary = false;
        draggableItems.push(
          Draggable.create(droppable, {
            bounds: '#dragging-content',
            type: 'x, y',
            cursor: 'grab',
            edgeResistance: isMobile ? 1.1 : 0.65,
            onPress() {
              if (bounds) return;
              bounds = getActivityBoundings();
            },
            onDrag() {
              if (!isMobile) return;
              const { pointerX, pointerY } = this;
              const hasTouched = hasTouchedBoundings(pointerX, pointerY, bounds, {
                marginBottom: null
              });
              if (hasTouched && !hasHitBoundary) {
                hasHitBoundary = true;
                self.handleDrop(this, this.target);
              }
            },
            onDragStart() {
              tLite.set(droppable, { cursor: 'grabbing' });
            },
            onDragEnd() {
              hasHitBoundary = false;
              self.handleDrop(this, this.target);
            }
          })[0]
        );
      });

      this.draggableLetters = draggableItems;
    },

    getPositionOfItems(element) {
      this.dragOriginalPosition = [...element].map(section => {
        const elementBound = section.getBoundingClientRect();

        return {
          xPosition: elementBound.x,
          yPosition: elementBound.y
        };
      });
    },

    handleDrop(draggable, dragTarget) {
      const keyGroupClassName = dragTarget.className.match(/Key[^\s]+/);
      const dragSource = document.getElementsByClassName(`placeholder-${keyGroupClassName}`)[0];
      let diffX = 0;
      let diffY = 0;
      let offsetX = 0;
      let offsetY = 0;

      if (draggable.hitTest(dragSource, '50%')) {
        diffX = dragTarget.getBoundingClientRect().left - dragSource.getBoundingClientRect().left;
        diffY = dragTarget.getBoundingClientRect().top - dragSource.getBoundingClientRect().top;

        offsetX = draggable.x - diffX;
        offsetY = draggable.y - diffY;

        TweenMax.to(dragTarget, 0.5, {
          x: offsetX - 1,
          y: offsetY + 0.3
        });

        draggable.disable();
        this.keyGroupSnapped += 1;
        this.sndBell();

        if (this.keyGroupSnapped === this.currentStep.letters.length) {
          this.handleRoundProgression();
        }
      } else {
        this.sndError();
        draggable.disable();
        TweenMax.to(dragTarget, 0.5, {
          x: 0,
          y: 0,
          onComplete: draggable.enable.bind(draggable)
        });
      }
    },

    getDropZone() {
      const dropWordsContainer = document.querySelectorAll('.drag-source');
      this.dropArea = [...dropWordsContainer].map((section, index) => {
        const elementBound = section.getBoundingClientRect();

        // Get X Position of drop zone - Position of draggable items
        this.dropXPosition = elementBound.x - this.dragOriginalPosition[index].xPosition;
        // Get Y Position of drop zone - Position of draggable items +
        // 50px left from top of keyboard
        this.dropYPosition = elementBound.y - this.dragOriginalPosition[index].yPosition;

        return {
          x: Math.floor(this.dropXPosition),
          y: Math.floor(this.dropYPosition)
        };
      });
    },

    updateDragElement(element) {
      const keyGroupClassName = element.className.match(/Key[^\s]+/);
      const dragSource = document.getElementsByClassName(`placeholder-${keyGroupClassName}`)[0];
      const dropBound = dragSource.getBoundingClientRect();
      const dragBound = element.getBoundingClientRect();

      this.dragSourceCoordinatesX = Math.floor(dropBound.x - dragBound.x);
      this.dragSourceCoordinatesY = Math.floor(dropBound.y - dragBound.y);
    },

    finishRound() {
      const hiddenKeys = document.getElementsByClassName('hide');
      const dragSources = document.getElementsByClassName('drag-source');
      const draggableKeyContainer = document.getElementsByClassName('draggable-key-container')[0];

      // remove drag sources from the DOM
      while (dragSources.length) {
        dragSources[0].parentNode.removeChild(dragSources[0]);
      }

      if (draggableKeyContainer) {
        // remove the key clones (draggable keys)
        draggableKeyContainer.innerHTML = '';
      }

      // show hidden keys from the keyboard
      while (hiddenKeys.length) {
        hiddenKeys[0].classList.remove('hide');
      }

      this.keyGroupSnapped = 0;
    },

    animateEndGame() {
      const timeline = new TimelineLite();
      const displacedKeys = document.getElementsByClassName('displaced-key');

      timeline.staggerTo(
        displacedKeys,
        1,
        {
          rotation: -15,
          transformOrigin: '50% 50%',
          ease: Power1.easeInOut,
          delay: 0.5
        },
        0,
        () => {
          timeline.staggerFromTo(
            displacedKeys,
            0.75,
            {
              rotation: -15,
              transformOrigin: '50% 50%',
              ease: Power1.easeInOut
            },
            {
              rotation: 15,
              yoyo: true,
              transformOrigin: '50% 50%',
              ease: Power1.easeInOut,
              repeat: -1
            },
            0
          );
        }
      );
    },

    handleRoundProgression() {
      if (this.isEndOfActivity) {
        this.animateEndGame();
        TweenMax.delayedCall(1, this.completeActivity.bind(this));
      } else {
        TweenMax.delayedCall(1, this.finishRound.bind(this));
        TweenMax.delayedCall(2, this.newRound.bind(this));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.build-a-keyboard {
  padding-top: 50px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  .target:after {
    background-image: none;
  }

  .keyboard-wrapper {
    position: relative;
    z-index: 0;
  }

  .draggable-key-container {
    text-align: center;

    &.scatter {
      height: 50%;
      width: 100%;
      position: relative;

      > div {
        position: absolute;
      }
    }

    .row {
      display: inline-block;
      position: relative;

      &.row0,
      &.row1,
      &.row2,
      &.row3,
      &.row4 {
        .active {
          background-color: transparent;
        }
      }

      > div {
        background-image: none;
      }
    }
  }

  .hide {
    display: none;
  }
}
#dragging-content {
  width: 100%;
  height: 100%;
}

@media only screen and (min-device-width: 700px) and (max-device-width: 1024px) {
  #dragging-content {
    width: 98%;
    height: 96%;
  }
}
</style>
